import type { BaseOptionType } from 'antd/es/select';
import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { useEffect, useState } from 'react';
import type { SelectOption } from '@/types';
import { DEBOUNCE_TIME } from '@/std/const/debounceTime';
import { filterOption } from '@/std/filter';
import { useDebouncedValue } from '@/std/hooks/useDebouncedValue';
import { apiProdutoNcmGet } from '../api/apiProdutoNcmGet';
import { ProdutoNcmStatus } from '../types/enum';

export function SelectProdutoNcm({
    ncmDescription,
    disabled,
    value,
    onChange,
}: {
    ncmDescription?: string | null;
    disabled?: boolean;
    value?: string | null;
    onChange?: (ncm: string | null, description: string | null, status: ProdutoNcmStatus) => void;
}) {
    const [enabled, setEnabled] = useState(false);
    const [search, setSearch] = useState('');
    const [searchDebounce] = useDebouncedValue(search, DEBOUNCE_TIME);
    const [pesquisando, setPesquisando] = useState(false);
    const [options, setOptions] = useState<{ value: string; label: string }[]>([]);

    const params = {
        orderby: 'prn_descricao, prn_ncm',
        pesquisar: searchDebounce,
    };

    const { data, isFetching } = useQuery({
        enabled: enabled && searchDebounce.length > 0,
        queryFn: () => apiProdutoNcmGet(params),
        queryKey: ['apiProdutoNcmGet', params],
    });

    useEffect(() => {
        setPesquisando(false);

        const isNcmNumerico = /^\d{8}$/.test(search);

        if (
            (!Array.isArray(data?.registros) || data?.registros.length === 0) &&
            value &&
            !isNcmNumerico
        ) {
            setOptions([{ label: `${value || ''} - ${ncmDescription || ''}`, value }]);
            return;
        }

        const newOptions: SelectOption<string>[] = [];

        const pesquisaConcluida = search === searchDebounce && !isFetching;
        const ncmEncontrado = data?.registros.some((ncm) => ncm.prn_ncm === search);

        if (isNcmNumerico && pesquisaConcluida && !ncmEncontrado) {
            newOptions.push({ label: `${search} - personalizado`, value: search });
        }

        if (data) {
            for (const ncm of data.registros) {
                newOptions.push({
                    label: `${ncm.prn_ncm} - ${ncm.prn_descricao}`,
                    value: ncm.prn_ncm,
                });
            }
        }

        setOptions(newOptions);
    }, [data, value, ncmDescription, search, searchDebounce, isFetching]);

    const handleChange = (
        ncm: string | undefined,
        option: BaseOptionType | BaseOptionType[] | undefined,
    ): void => {
        if (!onChange) {
            return;
        }

        if (!(ncm && option) || Array.isArray(option)) {
            onChange(null, null, ProdutoNcmStatus.Invalido);
            return;
        }

        if (option.label?.includes('Selecionar ncm:')) {
            onChange(ncm, '', ProdutoNcmStatus.Invalido);
            return;
        }

        const description =
            typeof option.label === 'string' ? option.label.split(' - ')[1] || '' : '';

        onChange(
            ncm,
            description,
            description === 'personalizado' ? ProdutoNcmStatus.Invalido : ProdutoNcmStatus.Valido,
        );
    };

    const handleSearch = (value: string): void => {
        setEnabled(true);
        setPesquisando(true);
        setSearch(value);
        setOptions([]);
    };

    return (
        <Select
            allowClear={true}
            className='w-full'
            disabled={disabled}
            loading={isFetching}
            notFoundContent={isFetching || pesquisando ? 'Pesquisando...' : undefined}
            onChange={handleChange}
            options={options}
            showSearch={{ filterOption, onSearch: handleSearch, searchValue: search }}
            value={value}
        />
    );
}
